<template>
  <div>
    <Header>Calendar Form</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <ValidationObserver v-slot="{ invalid, validate, errors, reset }">
        <form name="calendarForm" @submit.prevent="validate().then(() => onSubmit(invalid, reset))">
          <div class="grid gap-7 p-2">
            <ValidationProvider :rules="`required_if:nonDistrictBuildings,false`" name="district" v-slot="{ errors }" class="-mb-5">
              <label>
                District
              </label>
              <v-select 
                :disabled="!allDistricts || (typeof filledevent == 'object') || nonDistrictBuildings"
                :options="districtList"
                @input="getBuildings()"
                v-model="formValues.districtCode"
                multiple>
              </v-select>              
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <div class="inline-block flex flex-row gap-7">
                <label class="text-left block" :class="{'text-slate-500 cursor-not-allowed': multipleDistricts}">
                  <input type="checkbox" v-model="formValues.isBuildings" :disabled="multipleDistricts" :value="!multipleDistricts"/>
                  Is this event for specific schools or buildings?
                </label>
                <label class="text-left block" :class="{'text-slate-500 cursor-not-allowed': multipleDistricts}">
                  <input type="checkbox" v-model="nonDistrictBuildings" :disabled="multipleDistricts" :value="!multipleDistricts"/>
                  Submit events for non-district buildings?
                </label>
              </div>
          </div>
          <div v-if="formValues.isBuildings || nonDistrictBuildings" class="grid p-2">
            <ValidationProvider :rules="``" name="building" v-slot="{ errors }">
              <label>
                Building(s)
              </label>
              <v-select
                v-model="formValues.building"
                :options="schoolList"
                :getOptionLabel="school=>school.label"
                multiple
                :disabled="multipleDistricts"/>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
          <div class="p-2">
            <label class="text-left block">Event Information</label>
            <EventItem
              v-for="(item, index) in formValues.eventInfos" 
              :key="index"
              :value="item"
              :defaultValues="filledevent"
              @remove="formValues.eventInfos.splice(index, 1)"
              @input="(a) => onInput(item, [a.lowGrade, a.highGrade, a.eventType, a.time, a.eventDate, a.endDate, a.multipleDays, a.notes, a.timeDifference, a.schoolYear])"
            >
            </EventItem>
            <Button v-if="!filledevent"
              @click="addEventItem()"
              type="button"
              color="gray"
              size="md"
              class="rounded-md w-1/4 !shadow m-1">
              + Additional Event Information
            </Button>
            <div class="flex flex-col justify-center mx-auto items-center">
              <div class="w-full rounded-md bg-gray-200 p-2 my-2"
                v-if="!canSubmit">
                <span class="uppercase text-gray-500">
                  Missing Permissions to Submit Form
                </span>
              </div>
              <div class="flex flex-row">
                <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  v-if="displayFormHasErrors(errors)">
                  There are errors in fields above.
                </span>
              </div>
              <SubmitButton
                class="rounded-md font-medium text-white bg-sky-600 px-3 py-2 mt-2 hover:bg-blue-200 transition-colors shadow-sm"
                type="submit"
                color="blue"
                size="md"
                :disabled="!canSubmit"
                :submitting="submitting"
              >
                Submit
              </SubmitButton>
            </div>   
          </div> 
          <!-- Endpoint Data: {{ formValues }} -->
        </form>
      </ValidationObserver>
    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Main from '../components/Main.vue'
import Header from '../components/Header.vue'
import Button from '../components/Button.vue'
import SubmitButton from '../components/SubmitButton.vue'
import EventItem from '../components/items/EventItem.vue'

import cloneDeep from 'clone-deep'

import calendarFormService from '../services/formSubmission/calendarForm.js'

import user from '../services/user/user.js'
import view from '../services/user/view.js'

  export default {
    components: {
      Header,
      Main,
      SubmitButton,
      EventItem,
      Button,
    },
    data() {
      return {
        user: undefined,
        itemID: 0,
        formValues: {
          isBuildings: false, 
          districtCode: [], 
          eventInfos: [
            {
              id: this.itemID,
              lowGrade: '',
              highGrade: '',
              eventType: '',
              time: '',
              eventDate: '',
              endDate: '',
              notes: '',
              multipleDays: false,
              schoolYear: '',
            }
          ]
        },
        selectedSchools: [],
        schoolList: [],
        districtList: [],

        canSubmit: true,
        submitting: false,

        isPastEvent: false,
        submissionPromise: undefined,
        allDistricts: false,
        nonDistrictBuildings: false,
        independentBuildings: [],
        activeBuildings: [],
      }
    },
    props: ["filledevent"],
    methods: {
      async checkPastDate() {
        // form: all -> 0-13, detect duplicates
        let today = new Date()
        today.setHours(0, 0, 0, 0)
        for (let info of this.formValues.eventInfos) {
          let startDate = new Date(info.eventDate)
          let date = startDate.getDate() + 1
          startDate.setDate(date)
          if (startDate < today) {
            this.isPastEvent = confirm('Submit event in the past?')
            return this.isPastEvent
          }
        }
        return true
      },
      addEventItem() {
        this.itemID += 1
        this.formValues.eventInfos.push({
          id: this.itemID,
          lowGrade: '',
          highGrade: '',
          eventType: '',
          time: '',
          eventDate: '',
          endDate: '',
          notes: '',
          multipleDays: false,
          timeDifference: '',
          schoolYear: '',
        })
      },
      onInput(old, val) {
        old.lowGrade = val[0]
        old.highGrade = val[1]
        old.eventType = val[2]
        old.time = val[3]
        old.eventDate = val[4]
        old.endDate = val[5]
        old.multipleDays = val[6]
        old.notes = val[7]
        old.timeDifference = val[8]
        old.schoolYear = val[9]
        this.$forceUpdate()
      },
      getBuildings(){
        if (this.formValues.districtCode.length === 0) {
          this.schoolList = []
        }
        if (this.formValues.districtCode.length === 1 || this.nonDistrictBuildings) {
          this.schoolList = []
          let list = []
          // await getBuilding({'districtId': this.formValues.districtCode[0].value})
          //   .then(result => {
          //     list = result.data
          //   }, error => {
          //     console.error(error)
          //   })
          if (this.nonDistrictBuildings) {
            list = this.activeBuildings.find(el => el.code == 'no_district').buildings
          }
          else {
            list = this.activeBuildings.find(el => el.code == this.formValues.districtCode[0].value).buildings
          }
          for (const school of list) {
            let schoolObj = {
              value: school.code,
              label: school.name,
              id: school.id
            }
            this.schoolList.push(schoolObj)
          }
        }
      },
      displayFormHasErrors(errorsMap) {
        return Object.values(errorsMap).flat().length > 0
      },
      async onSubmit(formInvalid, reset) {
        if (formInvalid) {
          return false
        }

        let canSubmit = await this.checkPastDate()
        if (!canSubmit) {
          return false
        }

        this.submitting = true

        if (this.submissionPromise) {
          return false
        }
        let clonedFormValues = cloneDeep(this.formValues)
        for (const key of Object.keys(clonedFormValues)) {
          const camelToSnakeCase = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)

          clonedFormValues[camelToSnakeCase] = clonedFormValues[key]
          if (key == "eventInfos") {
            for (let i = 0; i < clonedFormValues[key].length; i++) {
              for (const newKey of Object.keys(clonedFormValues[key][i])) {
                const camelToSnakeCase = newKey.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
                clonedFormValues[key][i][camelToSnakeCase] = clonedFormValues[key][i][newKey]
              }
            }
          }
        }
        if (clonedFormValues.building) {
          let buildings = []
          for (const build of clonedFormValues.building) {
            buildings.push(build.id)
          }
          clonedFormValues.building = buildings
        }
        try {
          let response = await calendarFormService.postNewCalendarEvent([clonedFormValues]).finally(() => {
            this.submitting = false
            this.submissionPromise = undefined
            reset()
            // this.updateDateSelections()
          })
          if (response.status !== 201) {
            //  check for response.status
            throw new Error(response.statusText)
          }
          let body = await response.data // or .json() or whatever
          console.log(body)
          this.$router.push({
            name: 'EventList',
          })
        } catch (error) {
          console.log(error)
        }
      },
      invalidMessage(fields) {
        const fieldNames = Object.keys(fields)
        const listOfNames = fieldNames.map(fieldName => fieldName.replace('_', ' '))
        return `There are some errors above. Invalid fields: ${listOfNames}`
      },
      async setDefault() {
        if (this.filledevent) {
          if (this.filledevent.building) {
            this.formValues.isBuildings = true
            this.formValues.building = [{'value': this.filledevent.building.code, 'label': this.filledevent.building.name, 'id': this.filledevent.building.id}]
          }
          this.formValues.id = this.filledevent.id
          this.formValues.districtCode = [this.filledevent.district]
          await this.getBuildings()
        }
        if (!this.allDistricts) {
          this.view = await view.get()
          let districtView = this.view.getDistrict()
          for (let district of this.districtList) {
            if (districtView == district.value) {
              this.formValues.districtCode = [district]
            }
          }
        }
      },
      getDefault(index, key) {
        if (this.filledevent && index == 0) {
          return String(this.filledevent?.[key])
        } else {
          return ''
        }
      },
      getDefaultOverride (index) {
        if (this.filledevent && index == 0) {
          let ret = this.filledevent?.building ? true : false
          if (ret && !this.formValues.eventGroup?.[index]) {
            this.selectedSchools[index] = this.filledevent.building
          }
          return ret
        }
      },
      isBuildingOverride (index) {
        let ret = this.formValues?.eventGroup?.[index]?.buildingOverride
        if (ret === undefined) {
          return this.filledevent
        } else {
          if (!ret) {
            this.selectedSchools[index] = []
          }
          return ret
        }
      },
      updateSelectedSchools() {
        this.selectedSchools = this.formValues.eventGroup.map(group => group.building)
      },
      checkUserCanViewPage() {
        if(!this.user.can('view_calendar_event')) {
          this.$router.push({
            name: 'EventList'
          })
          return true
        }
      },
    },
    async mounted() {
      await user.ready()
      let res = await calendarFormService.getRoutedBuildingFromServer()
      this.activeBuildings = res.data
      for (const district of res.data) {
        if (district.code == 'no_district') {
          continue
        }
        let districtObj = {
          value: district.code,
          label: `${district.code}: ${district.name}`,
        }
        this.districtList.push(districtObj)
      }
      this.setDefault()
    },
    computed: {
      multipleDistricts() {
        return this.formValues.districtCode.length > 1
      }
    },
    watch: {
      nonDistrictBuildings(value) {
        if (value) {
          this.formValues.isBuildings = value
        }
        this.getBuildings()
      },
      'formValues.isBuildings'(value) {
        if (!value) {
          this.formValues.building = undefined
        }
      }
    },
    async created() {
      this.user = await user.get()
      if (this.checkUserCanViewPage()) {
        return
      }
      if (this.user.can('submit_calendar_event') && this.user.can('select_district')) {
        this.allDistricts = true
      }
      else {
        this.allDistricts = false
      }
    },
  }
</script>
